import React from 'react';
import {
  convertReferralPoints,
  fetchReferralAgents,
  fetchReferralDashboard,
} from '../api/referralApi';
import { isSuperAdminUser } from '../routes/ProtectedRoutes';
import api from '../api/axios';
import { getAdminLocale, t } from '../i18n';

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch (_) { return {}; }
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat(getAdminLocale(), {
      timeZone: 'Asia/Seoul',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(value));
  } catch (_) {
    return String(value);
  }
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString(getAdminLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-slate-900">{Number(value || 0).toLocaleString(getAdminLocale())}</div>
    </div>
  );
}

function RewardMetric({ label, value, suffix = t('명'), accent = false }) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
      <div className="text-sm text-slate-500">{label}</div>
      <div className={`mt-2 text-2xl font-bold ${accent ? 'text-amber-700' : 'text-slate-900'}`}>
        {suffix === 'USDT' ? formatMoney(value) : Number(value || 0).toLocaleString(getAdminLocale())} <span className="text-sm font-semibold">{suffix}</span>
      </div>
    </div>
  );
}

function ReferralRewardPanel({ reward, converting, onConvert }) {
  if (!reward?.eligible) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        {t('추천 유저 보상 포인트는 각 조직의')} <b>{t('lv2 에이전트 계정에만')}</b> {t('적립됩니다.')}
      </div>
    );
  }

  const policy = reward.policy || {};
  const batches = Array.isArray(reward.batches) ? reward.batches : [];
  const conversions = Array.isArray(reward.conversions) ? reward.conversions : [];
  const available = Number(reward.availablePoints || 0);

  return (
    <section className="space-y-4 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">{t('lv2 추천 보상 포인트')}</h2>
          <p className="mt-1 text-sm text-slate-600">
            {t('하부 조직 유저')} {Number(policy.usersPerReward || 30)}{t('명마다')} {formatMoney(policy.rewardAmount || 20)} {policy.currency || 'USDT'}{t('가 적립됩니다. 한 유저는 한 번만 스탬프 처리됩니다.')}
          </p>
        </div>
        <button
          type="button"
          onClick={onConvert}
          disabled={!reward.canConvert || converting || available <= 0}
          className="rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-white shadow-sm hover:bg-amber-400 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {converting ? t('변환 중...') : t('{amount} USDT 전액 자산으로 변환', { amount: formatMoney(available) })}
        </button>
      </div>

      {!reward.canConvert && (
        <div className="rounded-lg border border-amber-200 bg-white/80 px-3 py-2 text-xs text-amber-800">
          {t('포인트 현황은 조회할 수 있지만 자산 변환은 해당 lv2 에이전트 본인만 실행할 수 있습니다.')}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <RewardMetric label={t('스탬프 완료 유저')} value={reward.stampedUserCount} />
        <RewardMetric label={t('다음 보상 대기 유저')} value={reward.pendingUserCount} />
        <RewardMetric label={t('다음 20 USDT까지')} value={reward.nextRewardRemaining} />
        <RewardMetric label={t('사용 가능 포인트')} value={reward.availablePoints} suffix="USDT" accent />
        <RewardMetric label={t('누적 획득 포인트')} value={reward.lifetimeEarnedPoints} suffix="USDT" />
        <RewardMetric label={t('자산 변환 완료')} value={reward.convertedPoints} suffix="USDT" />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-800">{t('최근 포인트 적립')}</div>
          <div className="overflow-x-auto">
            <table className="admin-history-table min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr><th className="px-4 py-2">{t('배치')}</th><th className="px-4 py-2">{t('스탬프')}</th><th className="px-4 py-2">{t('적립')}</th><th className="px-4 py-2">{t('일시')}</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {batches.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">{t('아직 적립 기록이 없습니다.')}</td></tr>
                ) : batches.map((batch) => (
                  <tr key={batch.id}>
                    <td className="px-4 py-2 font-medium">#{batch.id}</td>
                    <td className="px-4 py-2">{Number(batch.userCount || 0).toLocaleString(getAdminLocale())}{t('명')}</td>
                    <td className="px-4 py-2 font-semibold text-amber-700">+{formatMoney(batch.rewardAmount)} USDT</td>
                    <td className="whitespace-nowrap px-4 py-2 text-slate-500">{formatDate(batch.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-800">{t('최근 자산 변환')}</div>
          <div className="overflow-x-auto">
            <table className="admin-history-table min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr><th className="px-4 py-2">{t('변환')}</th><th className="px-4 py-2">{t('금액')}</th><th className="px-4 py-2">{t('변환 후 자산')}</th><th className="px-4 py-2">{t('일시')}</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {conversions.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">{t('아직 변환 기록이 없습니다.')}</td></tr>
                ) : conversions.map((conversion) => (
                  <tr key={conversion.id}>
                    <td className="px-4 py-2 font-medium">#{conversion.id}</td>
                    <td className="px-4 py-2 font-semibold text-emerald-700">{formatMoney(conversion.amount)} USDT</td>
                    <td className="px-4 py-2">{conversion.assetBalanceAfter == null ? '-' : formatMoney(conversion.assetBalanceAfter)}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-slate-500">{formatDate(conversion.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AgentReferralManagement() {
  const currentUser = getStoredUser();
  const isSuperadmin = isSuperAdminUser(currentUser);
  const canSelectAgent = isSuperadmin || currentUser?.user_level === 'lv1';
  const [agents, setAgents] = React.useState([]);
  const [selectedAgentId, setSelectedAgentId] = React.useState('');
  const [dashboard, setDashboard] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [appliedQ, setAppliedQ] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [converting, setConverting] = React.useState(false);
  const [deletingUserCode, setDeletingUserCode] = React.useState('');
  const [error, setError] = React.useState('');

  const loadAgents = React.useCallback(async () => {
    if (!canSelectAgent) return;
    try {
      const data = await fetchReferralAgents();
      setAgents(Array.isArray(data?.agents) ? data.agents : []);
    } catch (err) {
      setError(err?.response?.data?.message || t('에이전트 목록을 불러오지 못했습니다.'));
    }
  }, [canSelectAgent]);

  const loadDashboard = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchReferralDashboard({
        page,
        pageSize: 20,
        q: appliedQ || undefined,
        agent_id: selectedAgentId || undefined,
      });
      setDashboard(data);
    } catch (err) {
      setDashboard(null);
      setError(err?.response?.data?.message || t('가입 현황을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [appliedQ, page, selectedAgentId]);

  React.useEffect(() => { loadAgents(); }, [loadAgents]);
  React.useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const copyLink = async () => {
    const url = dashboard?.agent?.referralUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      alert(t('홍보 링크가 복사되었습니다.'));
    } catch (_) {
      window.prompt(t('아래 링크를 복사해 주세요.'), url);
    }
  };

  const handleConvert = async () => {
    const available = Number(dashboard?.reward?.availablePoints || 0);
    if (!dashboard?.reward?.canConvert || available <= 0 || converting) return;
    const confirmed = window.confirm(t('{amount} USDT 포인트 전액을 현재 에이전트 자산으로 변환할까요?', { amount: formatMoney(available) }));
    if (!confirmed) return;

    setConverting(true);
    setError('');
    try {
      const result = await convertReferralPoints();
      alert(result?.message || t('포인트가 자산으로 변환되었습니다.'));
      window.dispatchEvent(new Event('admin:asset-summary-refresh'));
      await loadDashboard();
    } catch (err) {
      setError(err?.response?.data?.message || t('포인트 자산 변환에 실패했습니다.'));
    } finally {
      setConverting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!isSuperadmin || !user?.user_code || deletingUserCode) return;
    const label = user.nickname || user.id || user.user_code;
    const confirmed = window.confirm(`${label} (${user.user_code})\n${t('삭제')}?`);
    if (!confirmed) return;

    setDeletingUserCode(String(user.user_code));
    setError('');
    try {
      await api.post('/admin/users/delete', { target_id: user.user_code });
      await Promise.all([loadDashboard(), loadAgents()]);
    } catch (err) {
      alert(err?.response?.data?.message || err.message || t('서버 오류'));
    } finally {
      setDeletingUserCode('');
    }
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedQ(q.trim());
  };

  const summary = dashboard?.summary || {};
  const users = Array.isArray(dashboard?.users) ? dashboard.users : [];

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t('에이전트 가입 현황')}</h1>
        <p className="mt-1 text-sm text-slate-500">{t('자기 직속 유저와 모든 하위 에이전트 소속 유저를 합산해 확인합니다.')}</p>
      </div>

      {canSelectAgent && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <label className="block text-sm font-semibold text-slate-700">{t('조회할 에이전트')}</label>
          <select
            value={selectedAgentId}
            onChange={(event) => { setSelectedAgentId(event.target.value); setPage(1); }}
            className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm sm:max-w-xl"
          >
            <option value="">{t('내 계정')}</option>
            {agents.map((agent) => (
              <option key={agent.agentId} value={agent.agentId}>
                lv{agent.level} · {agent.agentCode} · {agent.agentName || agent.agentId} · {agent.status === 'active' ? t('활성') : t('비활성')} · {t('조직 전체')} {Number(agent.signupCount || 0).toLocaleString(getAdminLocale())}{t('명')} · {t('직속')} {Number(agent.directSignupCount || 0).toLocaleString(getAdminLocale())}{t('명')}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {dashboard?.agent && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t('에이전트 코드')}</div>
              <div className="mt-1 text-lg font-bold text-slate-900">{dashboard.agent.agentCode}</div>
              <div className="mt-1 flex items-center gap-2 text-sm text-slate-500"><span>{dashboard.agent.agentName || `Agent ${dashboard.agent.agentId}`}</span><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${dashboard.agent.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{dashboard.agent.status === 'active' ? t('활성') : t('비활성')}</span></div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t('전용 홍보 URL')}</div>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input readOnly value={dashboard.agent.referralUrl || ''} className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm" />
                <button type="button" onClick={copyLink} disabled={dashboard.agent.status !== 'active'} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40">{t('링크 복사')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <SummaryCard label={t('조직 전체 가입자')} value={summary.total} />
        <SummaryCard label={t('직속 가입자')} value={summary.directTotal} />
        <SummaryCard label={t('하부 가입자')} value={summary.downlineTotal} />
        <SummaryCard label={t('오늘 조직 가입자')} value={summary.today} />
        <SummaryCard label={t('최근 7일')} value={summary.last7Days} />
        <SummaryCard label={t('최근 30일')} value={summary.last30Days} />
      </div>

      {dashboard?.agent && (
        <ReferralRewardPanel
          reward={dashboard.reward}
          converting={converting}
          onConvert={handleConvert}
        />
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-4">
          <form onSubmit={submitSearch} className="flex flex-col gap-2 sm:flex-row">
            <input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder={t('유저 코드, 아이디, 닉네임, 소속 에이전트 검색')}
              className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500">{t('검색')}</button>
            <button type="button" onClick={() => { setQ(''); setAppliedQ(''); setPage(1); }} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700">{t('초기화')}</button>
          </form>
        </div>

        <div className="overflow-x-auto">
          <table className="admin-referral-table min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">{t('유저 코드')}</th>
                <th className="px-4 py-3">{t('아이디')}</th>
                <th className="px-4 py-3">{t('닉네임')}</th>
                <th className="px-4 py-3">{t('구분')}</th>
                <th className="px-4 py-3">{t('소속 에이전트')}</th>
                <th className="px-4 py-3">{t('포인트 스탬프')}</th>
                <th className="px-4 py-3">{t('상태')}</th>
                <th className="px-4 py-3">{t('가입일')}</th>
                {isSuperadmin && <th className="px-4 py-3 text-right">{t('관리')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={isSuperadmin ? 9 : 8} className="px-4 py-10 text-center text-slate-500">{t('불러오는 중...')}</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={isSuperadmin ? 9 : 8} className="px-4 py-10 text-center text-slate-500">{t('가입 유저가 없습니다.')}</td></tr>
              ) : users.map((user) => (
                <tr key={user.user_code} className="admin-referral-mobile-row hover:bg-slate-50">
                  <td data-label={t('유저 코드')} className="whitespace-nowrap px-4 py-3 font-medium text-slate-900">{user.user_code}</td>
                  <td data-label={t('아이디')} className="whitespace-nowrap px-4 py-3 text-slate-700">{user.id || '-'}</td>
                  <td data-label={t('닉네임')} className="whitespace-nowrap px-4 py-3 text-slate-700">{user.nickname || '-'}</td>
                  <td data-label={t('구분')} className="whitespace-nowrap px-4 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${user.relation_type === 'direct' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'}`}>
                      {user.relation_type === 'direct' ? t('직속') : t('하부')}
                    </span>
                  </td>
                  <td data-label={t('소속 에이전트')} className="whitespace-nowrap px-4 py-3 text-slate-700">
                    <div className="font-medium">{user.owner_agent_code || user.owner_agent_id || '-'}</div>
                    <div className="text-xs text-slate-400">lv{user.owner_agent_level || '-'} · {user.owner_agent_name || '-'}</div>
                  </td>
                  <td data-label={t('포인트 스탬프')} className="whitespace-nowrap px-4 py-3">
                    {user.reward_stamped ? (
                      <div>
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">{t('스탬프 완료')}</span>
                        <div className="mt-1 text-xs text-slate-400">{t('배치')} #{user.reward_batch_id || '-'} · {formatDate(user.reward_stamped_at)}</div>
                      </div>
                    ) : (
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">{t('대기')}</span>
                    )}
                  </td>
                  <td data-label={t('상태')} className="whitespace-nowrap px-4 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${user.is_banned ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {user.is_banned ? t('차단') : t('정상')}
                    </span>
                  </td>
                  <td data-label={t('가입일')} className="whitespace-nowrap px-4 py-3 text-slate-500">{formatDate(user.linked_at || user.date_of_sign_up)}</td>
                  {isSuperadmin && (
                    <td data-label={t('관리')} className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        type="button"
                        disabled={Boolean(deletingUserCode)}
                        onClick={() => handleDeleteUser(user)}
                        className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {String(deletingUserCode) === String(user.user_code) ? t('삭제 중...') : t('삭제')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
          <div className="text-sm text-slate-500">{t('총')} {Number(dashboard?.total || 0).toLocaleString(getAdminLocale())}{t('명')}</div>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40">{t('이전')}</button>
            <span className="px-2 py-1.5 text-sm text-slate-600">{page}</span>
            <button type="button" disabled={!dashboard?.hasMore || loading} onClick={() => setPage((value) => value + 1)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40">{t('다음')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
